import { AlertTriangle, RotateCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/Button';
import { CopyButton } from '@/components/ui/CopyButton';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/cn';
import type { ApiError } from '@shared/types';

/**
 * ErrorState — a failure the user can act on.
 *
 * Reads our `ApiError` envelope when it recognises one, so the message is the server's
 * and the requestId is on screen for support. Retry shows a loading state while the
 * caller's promise is in flight.
 */

export interface ErrorStateProps {
  /** An `ApiError`, an `Error`, a string — anything a query can reject with. */
  error?: unknown;
  onRetry?: () => void | Promise<unknown>;
  title?: ReactNode;
  /**
   * Override the derived message. Pass `null` to drop the line entirely — six
   * banners on one screen repeating "Something went wrong on our end." say it
   * once between them; the subordinate ones carry their title alone.
   */
  description?: ReactNode;
  /**
   * The `Reference …` chip — the id support asks people to quote.
   *
   * `false` drops it, for the same reason `description: null` exists and as its
   * necessary other half. On a screen whose seven requests fail together, one
   * outage produces one incident: seven reference chips under seven banners are
   * seven ids for one thing, and the copy that names "the reference" stops being
   * singular. The screen's first banner carries description + reference; its
   * subordinates carry title and recovery alone.
   */
  showReference?: boolean;
  /** Extra action beside Retry, e.g. "Report a problem". */
  secondaryAction?: ReactNode;
  /** One-line inline treatment, for a panel or a card body. */
  compact?: boolean;
  className?: string;
}

interface DerivedError {
  message: string;
  requestId: string | null;
  code: string | null;
  /** HTTP status when the failure came off the wire, `null` otherwise. */
  status: number | null;
}

function isApiError(value: unknown): value is ApiError {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false;
  const inner = (value as { error: unknown }).error;
  return typeof inner === 'object' && inner !== null && 'message' in inner;
}

function numeric(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function deriveError(error: unknown): DerivedError {
  if (isApiError(error)) {
    return {
      message: error.error.message,
      requestId: error.error.requestId ?? null,
      code: error.error.code ?? null,
      status: numeric((error.error as { status?: unknown }).status),
    };
  }
  // The client's `ApiError` is an Error subclass carrying the envelope's fields
  // flattened onto itself; read them before the generic Error branch, or every
  // failure that came through `lib/api` would lose its requestId.
  if (error instanceof Error) {
    const carrier = error as Error & { requestId?: unknown; code?: unknown; status?: unknown };
    return {
      message: error.message,
      requestId: typeof carrier.requestId === 'string' && carrier.requestId ? carrier.requestId : null,
      code: typeof carrier.code === 'string' ? carrier.code : null,
      status: numeric(carrier.status),
    };
  }
  if (typeof error === 'string' && error.trim()) {
    return { message: error, requestId: null, code: null, status: null };
  }
  return {
    message: 'The request failed before it finished. Retry, or reload if it keeps happening.',
    requestId: null,
    code: null,
    status: null,
  };
}

/**
 * What the user is told when the server fell over.
 *
 * A 5xx body is written for the log, not for the reader: `boom`, a constraint
 * name, a stack fragment — whatever the failing handler happened to put in the
 * field. Printing it as the panel's description makes an internal string into
 * product copy. Above 500 the panel says the one sentence the API itself uses
 * for this case, and the server's string moves down beside the request id, in
 * the mono line support already asks people to quote.
 */
const SERVER_FAULT_COPY =
  'The server had a problem completing that request. Try again — if it keeps happening, quote the reference below.';

function isServerFault(status: number | null): boolean {
  return status !== null && status >= 500;
}

/**
 * The reference the copy tells the reader to quote.
 *
 * A request id is the only string on that line worth anything: it is what
 * support greps for. When the response carried an `x-request-id` that is what
 * is shown; when it did not — a proxy 500, a connection that died before the
 * handler ran — a correlation id is minted here instead, so the sentence
 * "quote the reference below" is never printed above a Postgres constraint
 * name. `local-` marks the ones the browser made up, so nobody wastes a minute
 * searching the server's logs for one.
 *
 * The server's own string is not lost; it goes to the console beside the same
 * id (see the effect in `ErrorState`), where a diagnostic belongs.
 */
let mintedReferences = 0;

function mintReference(): string {
  mintedReferences += 1;
  return `local-${Date.now().toString(36)}-${mintedReferences.toString(36)}`;
}

export function ErrorState({
  error,
  onRetry,
  title,
  description,
  showReference = true,
  secondaryAction,
  compact = false,
  className,
}: ErrorStateProps) {
  const derived = deriveError(error);
  const serverFault = isServerFault(derived.status);
  /* `undefined` means "no opinion" and falls back to the written line for a
     server fault and to the server's own sentence otherwise — 4xx messages are
     written for the reader ("You do not have permission to do that."). `null`
     means "this banner does not repeat it". */
  const message =
    description === undefined ? (serverFault ? SERVER_FAULT_COPY : derived.message) : description;
  /* Kept, never lost — just moved off the panel and onto the console, keyed by
     the same reference the panel prints. A string like
     `relation "contacts_pkey" does not exist` is a diagnostic; rendering it at
     10px under a "Try again" button, unlabelled and uncontained, made it debris. */
  const detail = serverFault && derived.message.trim() ? derived.message.trim() : null;
  const referenceVisible = showReference && Boolean(derived.requestId || detail);
  /* One id per failure: a new error object is a new failure and earns a new
     reference; re-renders of the same one must not renumber it under the
     pointer that is about to copy it. */
  const referenceId = useMemo(() => deriveError(error).requestId ?? mintReference(), [error]);

  const [retrying, setRetrying] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!detail) return;
    console.warn(`[${referenceId}] ${detail}`);
  }, [detail, referenceId]);

  const handleRetry = useCallback(async () => {
    if (!onRetry || retrying) return;
    try {
      setRetrying(true);
      await onRetry();
    } finally {
      if (mounted.current) setRetrying(false);
    }
  }, [onRetry, retrying]);

  const retryButton = onRetry ? (
    <Button
      /*
       * `secondary`, not `primary`. Retrying is a RECOVERY, and gold is spent on
       * exactly two things in this product — the one action a surface exists to
       * enable, and a control's "on" state (docs/UI-PRIMITIVES.md, §"What gold
       * means"). "Try again" is the same kind of verb as "Clear search",
       * "Widen the range" and "Reload", all of which are `secondary`.
       *
       * This was the last gold recovery left, and it hid from every sweep
       * because it only paints when a request fails: the session-check failure
       * screen put a gold `Try again` beside a ghost `Sign in again` on a page
       * with no create action on it at all, so on that one screen the product's
       * single brand mark meant "retry". 22 call sites, one variant.
       * `scripts/invariants-check.mjs` now drives an aborted API and sweeps the
       * error states too.
       */
      variant={compact ? 'outline' : 'secondary'}
      size={compact ? 'sm' : 'md'}
      loading={retrying}
      iconLeft={<RotateCw className="size-4" aria-hidden />}
      onClick={handleRetry}
    >
      Try again
    </Button>
  ) : null;

  if (compact) {
    return (
      <div
        role="alert"
        className={cn(
          /*
            One geometry at every width. `flex-wrap` let the leftover pixels
            decide where "Try again" landed, so three of these side by side in
            one grid row — 384px, 284px, 484px — drew three different banners
            of the same component. The recovery now always sits below the
            message, which is the layout the narrowest card was going to get
            anyway.
          */
          'flex items-start gap-3 rounded-[var(--radius-md)] border border-line',
          'bg-[var(--danger-soft)] p-3',
          className,
        )}
      >
        <AlertTriangle
          className="mt-px size-4 shrink-0 text-[var(--danger)]"
          strokeWidth={1.75}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="text-base font-medium leading-snug text-primary">
            {title ?? 'Something went wrong'}
          </p>
          {message ? <p className="mt-1 text-sm leading-snug text-secondary">{message}</p> : null}
          {referenceVisible ? <Reference id={referenceId} className="mt-2" /> : null}
          {retryButton ? <div className="mt-2 flex">{retryButton}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div role="alert" className={cn('w-full', className)}>
      <EmptyState
        variant="error"
        title={title ?? 'We could not load this'}
        {...(message ? { description: message } : {})}
        action={retryButton}
        secondaryAction={secondaryAction}
        /* The reference belongs to this panel, so it sits `--space-6` under the
           recovery button rather than floating 57px below it — which is what the
           block's own 48px bottom padding plus a margin bought, and it read as
           debris that had fallen out of the panel. */
        className={referenceVisible ? 'pb-[var(--space-6)]' : undefined}
      />
      {referenceVisible ? (
        <div className="flex justify-center">
          <Reference id={referenceId} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * `Reference  a7f3c9…  ⧉` — a labelled, contained, copyable line.
 *
 * The thing support asks for, in the shape support asks for it. It used to be
 * the API's raw message printed bare at 10px with no label and no container,
 * under copy that called it "the reference": a reader following that instruction
 * quoted a Postgres identifier. The server's string is still kept — it goes to
 * the console keyed by this same id.
 */
function Reference({ id, className }: { id: string; className?: string }) {
  return (
    <div
      className={cn(
        'flex max-w-full items-center gap-[var(--space-2)] rounded-[var(--radius-sm)]',
        'border border-line-subtle bg-[var(--surface-2)]',
        'py-[var(--space-1)] pr-[var(--space-1)] pl-[var(--space-3)]',
        className,
      )}
    >
      <span className="shrink-0 text-2xs tracking-[var(--tracking-label)] text-muted uppercase">
        Reference
      </span>
      <span className="min-w-0 truncate font-mono text-xs text-secondary select-all">{id}</span>
      <CopyButton value={id} label="Copy the reference" size="sm" variant="ghost" />
    </div>
  );
}
